import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ExternalLink, ArrowRight, Home, Paintbrush, Droplets } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// DSGVO-konforme Referenzen ohne externe Bilder
const references = [
  {
    icon: Home,
    title: 'Holzdach-Konstruktion',
    description: 'Komplette Neukonstruktion eines traditionellen Holzdachs mit hochwertigen Materialien'
  },
  {
    icon: Paintbrush,
    title: 'Modernes Dachfenster-Projekt',
    description: 'Installation moderner Dachfenster mit optimaler Lichtausbeute und Energieeffizienz'
  },
  {
    icon: Droplets,
    title: 'Professionelle Dachsanierung',
    description: 'Vollständige Sanierung eines Wohngebäudes mit modernster Technik'
  }
];

export const ReferencesSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const navigate = useNavigate();

  return (
    <section id="referenzen" ref={ref} className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Referenzen &
            <span className="block bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Projekte
            </span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Überzeugen Sie sich von unserer Arbeit – Qualität, die für sich spricht
          </p>
        </motion.div>

        {/* References Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {references.map((ref, index) => (
            <ReferenceCard key={index} reference={ref} index={index} isInView={isInView} />
          ))}
        </div>

        {/* CTA to Gallery */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.button
            onClick={() => navigate('/galerie')}
            className="relative group px-12 py-5 bg-gradient-to-r from-red-600 to-red-800 text-white text-lg font-bold rounded-lg overflow-hidden shadow-xl inline-flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Zur Galerie</span>
            <ArrowRight className="relative z-10" size={24} />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
          <p className="text-gray-600 mt-4">Entdecken Sie unsere vollständige Projektgalerie</p>
        </motion.div>

        {/* Social Media Section */}
        <motion.div
          className="text-center py-16 bg-gradient-to-br from-gray-50 to-red-50 rounded-3xl"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-3xl font-bold mb-6 text-gray-900">
            Folgen Sie unserer Dachdecker-Journey
          </h3>
          <p className="text-gray-700 mb-8 text-lg">
            Bleiben Sie auf dem Laufenden über unsere neuesten Projekte und Innovationen
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <SocialButton platform="Instagram" link="https://www.instagram.com/mpfrankendach" />
            <SocialButton platform="Facebook" link="https://www.facebook.com/profile.php?id=61574644291943" />
          </div>
        </motion.div>

        {/* CTA Box */}
        <motion.div
          className="mt-16 bg-gradient-to-br from-gray-900 via-black to-red-950 rounded-3xl p-12 text-center relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          {/* Grain Overlay */}
          <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)"/%3E%3C/svg%3E")'
          }}></div>

          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Werden Sie unser nächstes
              <span className="block bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent mt-2">
                Erfolgsprojekt
              </span>
            </h3>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Lassen Sie uns gemeinsam Ihr Dachprojekt realisieren
            </p>
            <motion.button
              onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
              className="relative group px-10 py-4 bg-gradient-to-r from-red-600 to-red-800 text-white text-lg font-bold rounded-lg overflow-hidden shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10">Projekt besprechen</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ReferenceCard = ({ reference, index, isInView }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={isInView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.6, delay: index * 0.15 }}
    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
    whileHover={{ y: -10 }}
  >
    {/* Icon Placeholder instead of external image */}
    <div className="relative h-64 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="p-6 bg-gradient-to-br from-red-600 to-red-800 rounded-full">
          {React.createElement(reference.icon, { size: 48, className: 'text-white' })}
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
      
      {/* Hover Overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-red-600/90 to-red-800/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      >
        <ExternalLink className="text-white" size={48} />
      </motion.div>
    </div>

    {/* Content */}
    <div className="p-6">
      <h3 className="text-xl font-bold mb-2 text-gray-900">{reference.title}</h3>
      <p className="text-gray-600 leading-relaxed">{reference.description}</p>
    </div>
  </motion.div>
);

const SocialButton = ({ platform, link }) => (
  <motion.a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    className="px-8 py-3 bg-white text-gray-900 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all inline-block"
    whileHover={{ scale: 1.05, y: -3 }}
    whileTap={{ scale: 0.95 }}
  >
    {platform}
  </motion.a>
);